import { Injectable, inject } from '@angular/core';
import { InstancesService } from '../api/instances.service';
import { interval, BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

const DEFAULT_POLL_INTERVAL_MS = 15000;

@Injectable({
  providedIn: 'root'
})
export class InstancePollingService {
  private instancesService = inject(InstancesService);

  private activePolls = new Map<string, Subscription>();
  private lastUpdateTime = new Map<string, BehaviorSubject<Date | null>>();
  private pollingState = new Map<string, boolean>();

  startPolling(instanceId: string, shouldPoll: boolean = true, intervalMs: number = DEFAULT_POLL_INTERVAL_MS): void {
    const isCurrentlyPolling = this.pollingState.get(instanceId) || false;

    if (!shouldPoll) {
      if (isCurrentlyPolling) {
        this.stopPolling(instanceId);
      }
      return;
    }

    if (isCurrentlyPolling) {
      return;
    }

    if (!this.lastUpdateTime.has(instanceId)) {
      this.lastUpdateTime.set(instanceId, new BehaviorSubject<Date | null>(null));
    }

    const poll$ = interval(intervalMs)
      .pipe(
        switchMap(() => {
          const isStillActive = this.pollingState.get(instanceId);
          if (!isStillActive) {
            return of(null);
          }
          return this.instancesService.getInstanceById(instanceId);
        }),
        tap((data) => {
          if (data) {
            this.updateLastRefreshTime(instanceId);
            this.instancesService.triggerRefresh();
          }
        })
      )
      .subscribe({
        error: (err) => {
          this.stopPolling(instanceId);
        }
      });

    this.activePolls.set(instanceId, poll$);
    this.pollingState.set(instanceId, true);
  }

  updateLastRefreshTime(instanceId: string): void {
    if (!this.lastUpdateTime.has(instanceId)) {
      this.lastUpdateTime.set(instanceId, new BehaviorSubject<Date | null>(null));
    }
    const now = new Date();
    this.lastUpdateTime.get(instanceId)?.next(now);
  }

  stopPolling(instanceId: string): void {
    const poll = this.activePolls.get(instanceId);
    if (poll) {
      poll.unsubscribe();
      this.activePolls.delete(instanceId);
      this.pollingState.set(instanceId, false);
    }
  }

  stopAllPolling(): void {
    this.activePolls.forEach(poll => poll.unsubscribe());
    this.activePolls.clear();
  }

  getLastUpdateTime(instanceId: string): Observable<Date | null> {
    if (!this.lastUpdateTime.has(instanceId)) {
      this.lastUpdateTime.set(instanceId, new BehaviorSubject<Date | null>(null));
    }
    return this.lastUpdateTime.get(instanceId)!.asObservable();
  }

  resetTimer(instanceId: string): void {
    if (this.lastUpdateTime.has(instanceId)) {
      this.lastUpdateTime.get(instanceId)?.next(new Date());
    }
  }
}
